import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import './index.css';
import Index from './Index.tsx';
import { $customerAuth, refresh } from './lib/auth.ts';
import { $initialMessage } from './lib/chat.ts';
import { getShortID } from './router.ts';
import { $inRNWebView, $isEmbedded } from './lib/embed.ts';
import { Theme, updateTheme } from './lib/theme.ts';

const env = import.meta.env.VITE_ENV;
declare const window: any;

// Skip Sentry on the local dev server: deployed builds (stage/staging/live)
// still init normally, but `vite dev` shouldn't pollute the prod Sentry project
// — and browser tracking protection blocks the ingest endpoint anyway.
if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: 'https://3062e7631b4186157595a2ed83587c97@o255832.ingest.us.sentry.io/4509235331137537',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    ...(env !== 'live'
      ? {
          integrations: [
            Sentry.feedbackIntegration({
              colorScheme: 'system',
              autoInject: false,
            }),
          ],
        }
      : {}),
  });
}

const container = document.getElementById('root')!;
const root = createRoot(container!);

window.onload = async () => {
  try {
    const shortID = getShortID();

    if (shortID) {
      await refresh(shortID);
    }

    let payload: string | null = null;
    const rn = !!window.ReactNativeWebView;

    const searchParams = new URLSearchParams(location.search);
    const payloadBase64 = searchParams.get('payload');
    if (payloadBase64) {
      try {
        payload = atob(payloadBase64);
      } catch (error) {
        console.error('Failed to parse payload:', error);
      }
    } else if (rn) {
      $inRNWebView.set(true);
      payload = window.ReactNativeWebView?.injectedObjectJson() ?? '{}';
    }

    if (payload) {
      $isEmbedded.set(true);
      const { auth, colors, inset, message } = JSON.parse(payload);

      if (auth) {
        if (shortID) {
          $customerAuth.set({ ...$customerAuth.get(), [shortID]: auth });
        }
      }

      if (message?.length > 0) {
        $initialMessage.set(message);
      }

      if (colors) {
        updateTheme({
          backgroundColor: colors.background,
          textColor: colors.text,
        } as Theme);
      }
      if (inset) {
        document.documentElement.style.setProperty('--inset-bottom', `${inset.bottom}px`);
      }
    }
  } catch {
    // could not initialize
  }

  root.render(<Index />);
};
